import { useEffect } from 'react';
import { useWatch, useFormContext } from 'react-hook-form/dist/index.ie11';
import { useCorporateFormContext } from 'src/components/enterprise/features/Corporate/components/Form/CorporateFormProvider/useCorporateFormContext';
import { extractTrialLengthFromMonthlyFeeSelect } from 'src/components/enterprise/features/Corporate/components/Form/GrubhubSeamlessPlusWrapper/MembershipFeeSelectControl/mebershipFeeSelectControl.utils';
import { getTrialPeriodRange } from 'src/components/enterprise/features/Corporate/components/Form/GrubhubSeamlessPlusWrapper/grubhubSeamlessPlus.utils';
import { CorpSubscriptionFundingType } from 'src/types/Corp';
import { CorpInvoicingMethod } from 'src/components/enterprise/features/Corporate/components/Form/types/CorporateForm';

type UseGrubhubSeamlessPlusWrapperReturnType = {
  showCorpFundingControls: boolean;
  showMembershipPayerOptions: boolean;
  fundingOptionsDisabled: boolean;
  showTrialLengthControl: boolean;
  trialPeriodRange: string;
  showEmployeeFundedControls: boolean;
  disabledCorpFunding: boolean;
  showMembershipFeeSelectReadOnly: boolean;
};

export function useGrubhubSeamlessPlusWrapper(): UseGrubhubSeamlessPlusWrapperReturnType {
  const { isEditPage, corpData } = useCorporateFormContext();
  const { setValue } = useFormContext();
  const {
    ghPlusEnabled,
    'ghPlusSubscription.funding': funding,
    'ghPlusSubscription.trialLengthDays': trialLength,
    'ghPlusSubscription.monthlyFeeSelect': monthlyFeeSelect,
    invoicingMethod,
  } = useWatch({
    name: [
      'ghPlusEnabled',
      'ghPlusSubscription.funding',
      'ghPlusSubscription.trialLengthDays',
      'ghPlusSubscription.monthlyFeeSelect',
      'invoicingMethod',
    ],
  });

  const isCorpFunding = funding === CorpSubscriptionFundingType.CORP;
  const isEmployeeFunding = funding === CorpSubscriptionFundingType.EMPLOYEE;
  const trialLengthDaysValue = isEmployeeFunding
    ? extractTrialLengthFromMonthlyFeeSelect(monthlyFeeSelect)
    : trialLength;
  const startDate = corpData?.ghPlusSubscription?.startDate;
  const trialPeriodRange = getTrialPeriodRange(trialLengthDaysValue, startDate);

  const showTrialLengthControl =
    !isEditPage ||
    (isEditPage &&
      ghPlusEnabled &&
      isCorpFunding &&
      !corpData?.ghPlusSubscription?.trialLengthDays);

  const showMembershipFeeSelectReadOnly =
    isEditPage &&
    ghPlusEnabled &&
    isEmployeeFunding &&
    corpData?.ghPlusSubscription?.monthlyFee !== undefined;

  useEffect(() => {
    // this is required, since the field is hidden, when displaying it
    // React hook form doesn't register the value, so we need to set it manually (this is a known issue in RHC v6)
    if (showTrialLengthControl) {
      setValue('ghPlusSubscription.trialLengthDays', '');
    }
  }, [showTrialLengthControl, setValue]);

  return {
    trialPeriodRange,
    fundingOptionsDisabled: isEditPage && corpData?.ghPlusEnabled,
    showMembershipPayerOptions: ghPlusEnabled,
    showCorpFundingControls: isCorpFunding,
    showEmployeeFundedControls:
      isEmployeeFunding && !showMembershipFeeSelectReadOnly,
    showTrialLengthControl,
    disabledCorpFunding: invoicingMethod === CorpInvoicingMethod.MULTI_OPTION,
    showMembershipFeeSelectReadOnly,
  };
}
